/**
 * SyncStateStore — per-row `RowSyncState` keyed by `RowLocalId`.
 *
 * Tracks the in-flight status of background jobs populating a row's
 * cells. Mutation surface:
 *   - `setKeys(rowId, keys)` — seed a fresh `pending` state (e.g.
 *     when a row kicks off a new fetch).
 *   - `applyKey(rowId, syncKey, status)` — a per-key status update
 *     from the backend. Recomputes the aggregate and emits a
 *     `RowSyncStateChanged(rowId, syncKey)` event.
 *   - `clear(rowId)` — drop the row's state entirely (fetch cancelled).
 *
 * Reads return `null` for rows that have no registered sync state —
 * pure user-entered rows never get a `RowSyncState`.
 */

import type { Option } from "../core/option.ts";
import {
  Actor,
  type InvalidationSink,
  RowSyncStateChanged,
} from "../tx/invalidation.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { RowSyncState, type SyncStatus } from "../sync/sync-state.ts";
import { type SyncKey } from "../ids/ids.ts";

export class SyncStateStore {
  static readonly ACTOR = new Actor("SyncStateStore");

  readonly #byRow = new Map<RowLocalId, RowSyncState>();

  readonly #sink: InvalidationSink;
  constructor(sink: InvalidationSink) {
    this.#sink = sink;
  }

  /** Read. Returns `null` for rows with no registered sync state. */
  get(rowId: RowLocalId): Option<RowSyncState> {
    return this.#byRow.get(rowId) ?? null;
  }

  /** True if this row currently has any registered sync state. */
  has(rowId: RowLocalId): boolean {
    return this.#byRow.has(rowId);
  }

  /**
   * Seed a fresh `pending` state for `syncKeys`. Replaces any
   * existing state. Emits `RowSyncStateChanged(rowId, null)` —
   * `syncKey` is null because no single key drove the re-seed.
   */
  setKeys(rowId: RowLocalId, syncKeys: readonly SyncKey[]): void {
    this.#byRow.set(rowId, RowSyncState.fromKeys(syncKeys));
    this.#sink.emit(new RowSyncStateChanged(SyncStateStore.ACTOR, rowId, null));
  }

  /**
   * Apply a status update for one `syncKey`. No-op if the row has no
   * state or the key isn't registered. No-op if the status is
   * unchanged. Otherwise recomputes the aggregate and emits
   * `RowSyncStateChanged(rowId, syncKey)`.
   */
  applyKey(rowId: RowLocalId, syncKey: SyncKey, status: SyncStatus): void {
    const state = this.#byRow.get(rowId);
    if (!state) return;
    const ks = state.syncKeys.get(syncKey);
    if (!ks || ks.status === status) return;
    ks.status = status;
    RowSyncState.updateAggregate(state);
    this.#sink.emit(
      new RowSyncStateChanged(SyncStateStore.ACTOR, rowId, syncKey),
    );
  }

  /**
   * Drop this row's sync state entirely. No-op if no state is
   * registered. Emits `RowSyncStateChanged(rowId, null)`.
   */
  clear(rowId: RowLocalId): void {
    if (!this.#byRow.delete(rowId)) return;
    this.#sink.emit(new RowSyncStateChanged(SyncStateStore.ACTOR, rowId, null));
  }
}
